import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { TenantFeatureGuard } from '@/shared/components/TenantFeatureGuard'
import { TenantSiteGuard } from '@/shared/components/TenantSiteGuard'
import { ActivityBanner } from '@/shared/components/ActivityBanner'
import { AuthSessionBootstrap } from '@/features/auth/components/RequireAuth'
import { SiteHead } from '@/shared/components/SiteHead'
import { SiteFooter } from '@/shared/components/SiteFooter'
import { SiteHeader } from '@/shared/components/SiteHeader'
import { captureReferralFromSearch } from '@/shared/lib/referralCode'
import {
  SupportFloatDock,
  supportFloatVariantFromPath,
} from '@/shared/components/support/SupportFloatDock'

function isFullscreenStudioCanvas(pathname: string): boolean {
  const p = pathname.split('?')[0] ?? pathname
  return /\/studio\/productions\/[^/]+\/canvas$/.test(p) || /\/studio\/creative\/[^/]+\/canvas$/.test(p)
}

function isWriterScriptEditor(pathname: string): boolean {
  const p = pathname.split('?')[0] ?? pathname
  return /\/studio\/writer\/script\/\d+\/episodes\/\d+$/.test(p)
}

function isProductionReviewCenter(pathname: string): boolean {
  const p = pathname.split('?')[0] ?? pathname
  return /\/studio\/productions\/[^/]+\/reviews$/.test(p)
}

function isWorksWatchImmersive(pathname: string): boolean {
  const p = pathname.split('?')[0] ?? pathname
  return p === '/works/watch' || p.endsWith('/works/watch')
}

/**
 * 顶层布局：活动公告条 → SiteHeader → 主内容区滚动。
 */
export default function RootLayout() {
  const { pathname, search } = useLocation()
  const isAuth = pathname.startsWith('/auth')
  const isStudioCanvas = isFullscreenStudioCanvas(pathname)
  const isWriterEditor = isWriterScriptEditor(pathname)
  const isReviewCenter = isProductionReviewCenter(pathname)
  const isWorksWatch = isWorksWatchImmersive(pathname)
  const hideChrome = isAuth || isStudioCanvas || isReviewCenter || isWorksWatch
  const immersiveLayout = isStudioCanvas || isWriterEditor || isReviewCenter || isWorksWatch
  const floatVariant = supportFloatVariantFromPath(pathname)

  useEffect(() => {
    if (search) captureReferralFromSearch(search)
  }, [search])

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-[#0b0b13] text-zinc-100">
      <SiteHead />
      <AuthSessionBootstrap />
      {!hideChrome && (
        <>
          <ActivityBanner />
          <SiteHeader />
        </>
      )}
      <main className={immersiveLayout ? 'min-h-0 flex-1 overflow-hidden' : 'min-h-0 flex-1 overflow-y-auto'}>
        <div className={immersiveLayout ? 'flex h-full flex-col' : 'flex min-h-full flex-col'}>
          <div className={immersiveLayout ? 'h-full flex-1' : 'flex-1'}>
            <TenantSiteGuard>
              <TenantFeatureGuard>
                <Outlet />
              </TenantFeatureGuard>
            </TenantSiteGuard>
          </div>
          {!hideChrome && !isWriterEditor && !isReviewCenter && !isWorksWatch ? <SiteFooter /> : null}
        </div>
      </main>
      {!hideChrome && !isWriterEditor && !isReviewCenter && !isWorksWatch ? (
        <SupportFloatDock variant={floatVariant} />
      ) : null}
    </div>
  )
}
